import React, { useEffect, useRef, useState } from 'react';
import { useVendorBranding } from '../context/VendorBrandingContext';
import { formatCurrency } from '../config/currency';
import {
  CUSTOMER_DISPLAY_CHANNEL,
  CustomerDisplayLine,
  CustomerDisplayMessage,
} from '../edge/useCustomerDisplayBroadcast';

/**
 * CustomerDisplay — /customer-display
 *
 * Non-interactive customer-facing screen for the dual-screen edge till
 * (11.6" 1920x1080, read at ~1m). Rendered fullscreen with no Layout chrome
 * and no auth guard (it is a local, physically-secured screen).
 *
 * Data source: BroadcastChannel bridge published by the staff screen (same
 * machine, same origin) via useCustomerDisplayBroadcast in CartContext.
 *
 * States: idle (branding + Welcome) → active (itemised order) → thank-you
 * (after settle, reverts to idle after 8s).
 */

const THANK_YOU_MS = 8_000;

type DisplayState =
  | { mode: 'idle' }
  | { mode: 'active'; lines: CustomerDisplayLine[]; total: number }
  | { mode: 'thankyou' };

const rootStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#1C1917', /* basil-todo */
  color: '#FAFAF9', /* basil-todo */
  fontFamily: 'inherit',
  overflow: 'hidden',
  userSelect: 'none',
  cursor: 'default',
};

const moneyStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
};

const CenterStage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: 48,
    }}
  >
    {children}
  </div>
);

const CustomerDisplay: React.FC = () => {
  const { branding } = useVendorBranding();
  const [display, setDisplay] = useState<DisplayState>({ mode: 'idle' });
  const thankYouTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL);
    const onMessage = (event: MessageEvent) => {
      const message = event.data as CustomerDisplayMessage | undefined;
      if (!message || typeof message !== 'object') return;

      if (message.type === 'cart') {
        if (thankYouTimerRef.current) {
          clearTimeout(thankYouTimerRef.current);
          thankYouTimerRef.current = null;
        }
        if (Array.isArray(message.lines) && message.lines.length > 0) {
          setDisplay({ mode: 'active', lines: message.lines, total: message.total ?? 0 });
        } else {
          setDisplay({ mode: 'idle' });
        }
      } else if (message.type === 'settled') {
        setDisplay({ mode: 'thankyou' });
        if (thankYouTimerRef.current) clearTimeout(thankYouTimerRef.current);
        thankYouTimerRef.current = setTimeout(() => {
          thankYouTimerRef.current = null;
          setDisplay({ mode: 'idle' });
        }, THANK_YOU_MS);
      }
    };

    channel.addEventListener('message', onMessage as EventListener);
    return () => {
      channel.removeEventListener('message', onMessage as EventListener);
      channel.close();
      if (thankYouTimerRef.current) {
        clearTimeout(thankYouTimerRef.current);
        thankYouTimerRef.current = null;
      }
    };
  }, []);

  const vendorName = branding.vendorName || 'SOG';

  if (display.mode === 'idle') {
    return (
      <div style={rootStyle} data-testid="customer-display-idle">
        <CenterStage>
          {branding.logo ? (
            <img
              src={branding.logo}
              alt={vendorName}
              style={{ maxHeight: 220, maxWidth: '40%', marginBottom: 40, objectFit: 'contain' }}
            />
          ) : (
            <div style={{ fontSize: 96, fontWeight: 700, marginBottom: 24, letterSpacing: 2 }}>
              {vendorName}
            </div>
          )}
          <div style={{ fontSize: 56, fontWeight: 300, color: '#D6D3D1' }}>Welcome</div> {/* basil-todo */}
        </CenterStage>
      </div>
    );
  }

  if (display.mode === 'thankyou') {
    return (
      <div style={rootStyle} data-testid="customer-display-thankyou">
        <CenterStage>
          <div style={{ fontSize: 96, fontWeight: 600 }}>Thank you</div>
          <div style={{ fontSize: 40, fontWeight: 300, marginTop: 24, color: '#D6D3D1' }}> {/* basil-todo */}
            See you again soon
          </div>
        </CenterStage>
      </div>
    );
  }

  const { lines, total } = display;

  return (
    <div style={rootStyle} data-testid="customer-display-active">
      {/* Header */}
      <div
        style={{
          padding: '24px 48px',
          fontSize: 34,
          fontWeight: 600,
          borderBottom: '1px solid #44403C', /* basil-todo */
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{vendorName}</span>
        <span style={{ fontSize: 28, fontWeight: 400, color: 'var(--text-tertiary)' }}>Your order</span>
      </div>

      {/* Itemised lines */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 48px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 34 }}>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #292524' }}> {/* basil-todo */}
                <td style={{ padding: '18px 0', fontWeight: 500 }}>{line.name}</td>
                <td
                  style={{
                    padding: '18px 24px',
                    textAlign: 'right',
                    color: 'var(--text-tertiary)',
                    whiteSpace: 'nowrap',
                    ...moneyStyle,
                  }}
                >
                  {line.quantity} × {formatCurrency(line.unitPrice)}
                </td>
                <td
                  style={{
                    padding: '18px 0',
                    textAlign: 'right',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    ...moneyStyle,
                  }}
                >
                  {formatCurrency(line.lineTotal)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Running total */}
      <div
        style={{
          padding: '32px 48px',
          borderTop: '2px solid #44403C', /* basil-todo */
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          backgroundColor: '#292524', /* basil-todo */
        }}
      >
        <div>
          <div style={{ fontSize: 40, fontWeight: 600 }}>Total</div>
          <div style={{ fontSize: 22, color: 'var(--text-tertiary)', marginTop: 4 }}>
            All prices include VAT
          </div>
        </div>
        <div style={{ fontSize: 88, fontWeight: 700, ...moneyStyle }} data-testid="customer-display-total">
          {formatCurrency(total)}
        </div>
      </div>
    </div>
  );
};

export default CustomerDisplay;
